'use client';

/**
 * StartWorkoutCard — the dominant "Today's Order / Start Workout" CTA,
 * extracted from ProgramDashboard so every persona dashboard (Full,
 * Operator, Lean) shares one source of truth for the start-workout
 * logic (deload / rest-day / full-intensity branches).
 *
 * `label` lets a persona reframe the primary verb — Operator says
 * "Deploy", others say "Start Workout" — without forking the logic.
 */

import type { DashboardData } from '@/lib/hooks/useDashboardData';

export default function StartWorkoutCard({
  data,
  label = 'Start Workout',
}: {
  data: DashboardData;
  label?: string;
}) {
  const {
    currentWeek, currentDay, currentPhase,
    needsDeload, dismissedDeload, deloadReason, todayRecovery,
    recoveryDebt, startTodayWorkout, startDeloadedWorkout,
  } = data;

  // When ACWR is severe AND deload is recommended, the advisory already
  // shows a REST card — hide the "Start Deloaded" CTA to avoid telling
  // the user to rest and deload at the same time.
  const restDayRecommended = needsDeload && recoveryDebt &&
    (recoveryDebt.debtLevel === 'severe' || recoveryDebt.acwrStatus === 'dangerous');

  return (
    <section className="relative overflow-hidden rounded-xl border border-amber-500/40 bg-hud-ink/85 shadow-[0_0_40px_-16px_rgba(245,158,11,0.4)]">
      <div aria-hidden className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-amber-500/80 to-transparent" />
      <span aria-hidden className="pointer-events-none absolute top-0 left-0 w-3 h-3 border-t border-l border-amber-500/60" />
      <span aria-hidden className="pointer-events-none absolute top-0 right-0 w-3 h-3 border-t border-r border-amber-500/60" />
      <span aria-hidden className="pointer-events-none absolute bottom-0 left-0 w-3 h-3 border-b border-l border-amber-500/60" />
      <span aria-hidden className="pointer-events-none absolute bottom-0 right-0 w-3 h-3 border-b border-r border-amber-500/60" />

      <header className="px-4 py-1.5 flex items-center justify-between border-b border-amber-500/15 font-mono text-[9px] uppercase tracking-[0.28em]">
        <div className="flex items-center gap-2 text-amber-500/90">
          <span className="w-1 h-1 rounded-full bg-hud-cyan animate-hud-blink" />
          <span>Today&apos;s Order</span>
        </div>
        <span className="text-slate-500 tabular-nums">
          W{currentWeek}<span className="text-slate-700"> · </span>D{currentDay}
        </span>
      </header>

      <div className="px-4 py-4 flex flex-col items-center text-center">
        <div className={`font-mono text-[10px] uppercase tracking-[0.22em] mb-3 ${currentPhase.color}`}>
          {currentPhase.name}
        </div>

        {needsDeload && !dismissedDeload && (
          <div className="mb-3 inline-flex items-center gap-1.5 px-2 py-0.5 border border-amber-500/40 bg-amber-500/[0.06] font-mono text-[10px] uppercase tracking-[0.18em] text-amber-400">
            <span className="w-1.5 h-1.5 bg-amber-400 rounded-full animate-hud-blink" />
            Deload: {deloadReason}
          </div>
        )}
        {!todayRecovery && !restDayRecommended && !needsDeload && (
          <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500 mb-3">
            Recovery not logged today
          </div>
        )}

        <div className="flex flex-wrap items-center justify-center gap-2">
          {restDayRecommended ? (
            <>
              <button
                onClick={startDeloadedWorkout}
                className="px-5 py-2.5 rounded-md bg-slate-950 border border-amber-500/50 text-amber-400 font-mono font-bold tracking-[0.18em] text-[11px] uppercase hover:bg-amber-500/10 transition"
              >
                Train Anyway (Deloaded)
              </button>
              <button
                onClick={() => startTodayWorkout(false, true)}
                className="px-4 py-2 rounded-md bg-slate-950 border border-slate-800 text-slate-500 font-mono font-semibold tracking-[0.18em] text-[10px] uppercase hover:text-slate-300 hover:border-slate-700 transition"
              >
                Full Intensity
              </button>
            </>
          ) : needsDeload && !dismissedDeload ? (
            <>
              <button
                onClick={startDeloadedWorkout}
                className="px-6 py-3 rounded-md bg-amber-500 text-hud-ink font-mono font-bold tracking-[0.22em] text-sm uppercase hover:bg-amber-400 transition shadow-[0_0_24px_-6px_rgba(245,158,11,0.7)]"
              >
                Start Deloaded
              </button>
              <button
                onClick={() => startTodayWorkout(false, true)}
                className="px-5 py-2.5 rounded-md bg-slate-950 border border-slate-700 text-slate-200 font-mono font-semibold tracking-[0.18em] text-[11px] uppercase hover:bg-slate-900 transition"
              >
                Full Intensity
              </button>
            </>
          ) : (
            <button
              onClick={() => startTodayWorkout()}
              className="px-10 py-3.5 rounded-md bg-amber-500 text-hud-ink font-mono font-bold tracking-[0.22em] text-sm uppercase hover:bg-amber-400 transition shadow-[0_0_24px_-6px_rgba(245,158,11,0.7)]"
            >
              {label}
            </button>
          )}
        </div>
      </div>
    </section>
  );
}
